import type { z } from 'zod';
import type {
  SceneBounds,
  SceneLayout,
  ScenePoint,
  SceneRelation,
} from '../../contracts/index.ts';
import type { NormalizedSceneNode } from '../ir/types.ts';
import type { SceneCapabilityDescriptor } from './types.ts';
import dagre from '@dagrejs/dagre';
import { z as zod } from 'zod';
import { serializeZodSchema } from '../../catalog/ProtocolSchemaRegistry.ts';
import { SCENE_LIMITS } from '../contracts/limits.ts';

export interface SceneLayoutContext {
  viewport: SceneBounds;
  relations: SceneRelation[];
}

export interface SceneLayoutDefinition {
  descriptor: SceneCapabilityDescriptor;
  schema: z.ZodTypeAny;
  apply: (
    nodes: NormalizedSceneNode[],
    config: unknown,
    context: SceneLayoutContext,
  ) => NormalizedSceneNode[];
}

const capabilityKeyPattern = /^probfun\.scene\.layout\.[a-z0-9-]+@1$/;

export class LayoutRegistry {
  private readonly entries = new Map<string, SceneLayoutDefinition>();
  private readonly cache = new Map<string, NormalizedSceneNode[]>();

  register<TSchema extends z.ZodTypeAny>(definition: {
    descriptor: SceneCapabilityDescriptor;
    schema: TSchema;
    apply: (
      nodes: NormalizedSceneNode[],
      config: z.output<TSchema>,
      context: SceneLayoutContext,
    ) => NormalizedSceneNode[];
  }): this {
    if (!capabilityKeyPattern.test(definition.descriptor.key))
      throw new Error(`布局能力 key 非法：${definition.descriptor.key}`);
    if (definition.descriptor.category !== 'layout')
      throw new Error('布局能力 Descriptor 的 category 必须是 layout');
    if (this.entries.has(definition.descriptor.key))
      throw new Error(`布局能力已经注册：${definition.descriptor.key}`);
    this.entries.set(definition.descriptor.key, {
      descriptor: definition.descriptor,
      schema: definition.schema,
      apply: (nodes, config, context) => definition.apply(
        nodes,
        definition.schema.parse(config),
        context,
      ),
    });
    return this;
  }

  resolve(key: string): SceneLayoutDefinition {
    const entry = this.entries.get(key);
    if (!entry)
      throw new Error(`未知布局能力：${key}`);
    return entry;
  }

  listDescriptors(): SceneCapabilityDescriptor[] {
    return [...this.entries.values()].map(entry =>
      structuredClone(entry.descriptor),
    );
  }

  apply(
    layout: SceneLayout,
    nodes: NormalizedSceneNode[],
    context: SceneLayoutContext,
  ): NormalizedSceneNode[] {
    const entry = this.resolve(layout.capability);
    const cacheKey = JSON.stringify({
      capability: layout.capability,
      config: layout.config,
      viewport: context.viewport,
      nodes: nodes.map(node => [
        node.id,
        node.parentId,
        node.bounds,
        node.transform.x,
        node.transform.y,
      ]),
      relations: context.relations.map(relation => [
        relation.id,
        relation.sourceId,
        relation.targetId,
        relation.type,
      ]),
    });
    const cached = this.cache.get(cacheKey);
    if (cached)
      return structuredClone(cached);
    const result = entry.apply(nodes, layout.config, context);
    if (this.cache.size >= 100)
      this.cache.delete(this.cache.keys().next().value!);
    this.cache.set(cacheKey, structuredClone(result));
    return result;
  }
}

const baseBoxSchema = zod.object({
  x: zod.number().finite().default(0),
  y: zod.number().finite().default(0),
  width: zod.number().positive().max(SCENE_LIMITS.maxWorldSize).default(800),
  height: zod.number().positive().max(SCENE_LIMITS.maxWorldSize).default(450),
  gap: zod.number().nonnegative().max(10000).default(16),
  padding: zod.number().nonnegative().max(10000).default(0),
}).strict();

type BaseBox = z.infer<typeof baseBoxSchema>;

function descriptor(
  key: string,
  title: string,
  description: string,
  schema: z.ZodType<unknown>,
  cost: SceneCapabilityDescriptor['cost'] = 'low',
  examples: SceneCapabilityDescriptor['examples'] = [{}],
  constraints: string[] = [],
): SceneCapabilityDescriptor {
  return {
    key,
    category: 'layout',
    title,
    description,
    suitableFor: ['二维教学场景', '数据实体', '响应式布局'],
    inputs: ['目标节点', '布局边界', '确定性配置'],
    outputs: ['节点 transform'],
    compatibleWith: ['probfun.scene-grammar@1'],
    configSchema: serializeZodSchema(schema, key),
    constraints: [
      `单次最多处理 ${SCENE_LIMITS.maxRuntimeNodes} 个节点`,
      '相同输入必须产生相同坐标',
      ...constraints,
    ],
    determinism: ['稳定输入顺序、固定参数和固定迭代次数产生相同坐标。'],
    incrementalStrategy: ['节点身份不变时只更新受影响节点的 transform。'],
    diagnostics: ['未知目标、非法配置和超限通过 SceneDiagnostic 返回。'],
    cost,
    examples,
    counterExamples: [{ callback: '() => random()' }],
    commonErrors: ['目标节点不存在', '布局边界不足', '配置超过安全上限'],
    aiGuidance: ['优先复用布局能力，不为具体案例硬编码坐标。'],
    repairGuidance: ['减少节点数、增大边界或改用更简单布局。'],
  };
}

function positioned(
  nodes: NormalizedSceneNode[],
  positions: Map<string, ScenePoint>,
): NormalizedSceneNode[] {
  return nodes.map((node) => {
    const point = positions.get(node.id);
    return point
      ? {
          ...node,
          transform: {
            ...node.transform,
            x: point.x,
            y: point.y,
          },
        }
      : node;
  });
}

function nodeSize(node: NormalizedSceneNode): { width: number; height: number } {
  return {
    width: Math.max(node.bounds.width, 1),
    height: Math.max(node.bounds.height, 1),
  };
}

function linearLayout(
  axis: 'x' | 'y',
  nodes: NormalizedSceneNode[],
  config: BaseBox,
  reverse = false,
): NormalizedSceneNode[] {
  const values = reverse ? [...nodes].reverse() : nodes;
  let cursor = (axis === 'x' ? config.x : config.y) + config.padding;
  const positions = new Map<string, ScenePoint>();
  for (const node of values) {
    const size = nodeSize(node);
    positions.set(node.id, {
      x: axis === 'x'
        ? cursor
        : config.x + config.padding,
      y: axis === 'y'
        ? cursor
        : config.y + config.padding,
    });
    cursor += (axis === 'x' ? size.width : size.height) + config.gap;
  }
  return positioned(nodes, positions);
}

function registerSimpleLinear(
  registry: LayoutRegistry,
  name: 'row' | 'column' | 'queue' | 'flow',
  title: string,
  axis: 'x' | 'y',
): void {
  registry.register({
    descriptor: descriptor(
      `probfun.scene.layout.${name}@1`,
      title,
      `${title}，保留目标节点稳定身份。`,
      baseBoxSchema,
    ),
    schema: baseBoxSchema,
    apply: (nodes, config) => linearLayout(axis, nodes, config),
  });
}

export function createBuiltinLayoutRegistry(): LayoutRegistry {
  const registry = new LayoutRegistry();

  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.absolute@1',
      '绝对定位',
      '保留节点自身 transform，不执行重新排布。',
      zod.object({}).strict(),
    ),
    schema: zod.object({}).strict(),
    apply: nodes => nodes,
  });

  registerSimpleLinear(registry, 'row', '水平行', 'x');
  registerSimpleLinear(registry, 'column', '垂直列', 'y');
  registerSimpleLinear(registry, 'queue', '有序队列', 'x');

  const flowSchema = baseBoxSchema;
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.flow@1',
      '响应式流程排布',
      '按可用宽度自动换行，适用于流程与窄屏重排。',
      flowSchema,
    ),
    schema: flowSchema,
    apply: (nodes, config) => {
      const positions = new Map<string, ScenePoint>();
      const maximumX = config.x + config.width - config.padding;
      let cursorX = config.x + config.padding;
      let cursorY = config.y + config.padding;
      let rowHeight = 0;
      for (const node of nodes) {
        const size = nodeSize(node);
        if (
          cursorX > config.x + config.padding
          && cursorX + size.width > maximumX
        ) {
          cursorX = config.x + config.padding;
          cursorY += rowHeight + config.gap;
          rowHeight = 0;
        }
        positions.set(node.id, { x: cursorX, y: cursorY });
        cursorX += size.width + config.gap;
        rowHeight = Math.max(rowHeight, size.height);
      }
      return positioned(nodes, positions);
    },
  });

  const gridSchema = baseBoxSchema.extend({
    columns: zod.number().int().positive().max(1000).default(4),
    rowGap: zod.number().nonnegative().max(10000).optional(),
    columnGap: zod.number().nonnegative().max(10000).optional(),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.grid@1',
      '规则网格',
      '按稳定输入顺序放入固定列数网格。',
      gridSchema,
    ),
    schema: gridSchema,
    apply: (nodes, config) => {
      const columnGap = config.columnGap ?? config.gap;
      const rowGap = config.rowGap ?? config.gap;
      const availableWidth = config.width - config.padding * 2;
      const cellWidth = Math.max(
        1,
        (availableWidth - columnGap * (config.columns - 1)) / config.columns,
      );
      const positions = new Map<string, ScenePoint>();
      let rowY = config.y + config.padding;
      for (let index = 0; index < nodes.length; index += 1) {
        const column = index % config.columns;
        const row = Math.floor(index / config.columns);
        if (column === 0 && row > 0) {
          const previousRow = nodes.slice(
            (row - 1) * config.columns,
            row * config.columns,
          );
          rowY += Math.max(
            ...previousRow.map(node => nodeSize(node).height),
            1,
          ) + rowGap;
        }
        positions.set(nodes[index]!.id, {
          x: config.x + config.padding + column * (cellWidth + columnGap),
          y: rowY,
        });
      }
      return positioned(nodes, positions);
    },
  });

  const stackSchema = baseBoxSchema.extend({
    offsetX: zod.number().finite().default(8),
    offsetY: zod.number().finite().default(8),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.stack@1',
      '层叠',
      '按稳定顺序应用固定偏移并保持 zIndex。',
      stackSchema,
    ),
    schema: stackSchema,
    apply: (nodes, config) => positioned(nodes, new Map(
      nodes.map((node, index) => [node.id, {
        x: config.x + config.padding + index * config.offsetX,
        y: config.y + config.padding + index * config.offsetY,
      }]),
    )),
  });

  const radialSchema = baseBoxSchema.extend({
    startAngle: zod.number().finite().default(-90),
    endAngle: zod.number().finite().default(270),
    radius: zod.number().positive().max(SCENE_LIMITS.maxWorldSize).optional(),
  }).strict();
  const radialApply = (
    nodes: NormalizedSceneNode[],
    config: z.infer<typeof radialSchema>,
    closeRing: boolean,
  ) => {
    const centerX = config.x + config.width / 2;
    const centerY = config.y + config.height / 2;
    const radius = config.radius
      ?? Math.max(1, Math.min(config.width, config.height) / 2 - config.padding);
    const denominator = closeRing
      ? Math.max(nodes.length, 1)
      : Math.max(nodes.length - 1, 1);
    const positions = new Map<string, ScenePoint>();
    nodes.forEach((node, index) => {
      const angle = (
        config.startAngle
        + (config.endAngle - config.startAngle) * index / denominator
      ) * Math.PI / 180;
      positions.set(node.id, {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      });
    });
    return positioned(nodes, positions);
  };
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.radial@1',
      '径向',
      '在指定角度范围内径向排布。',
      radialSchema,
    ),
    schema: radialSchema,
    apply: (nodes, config) => radialApply(nodes, config, false),
  });
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.ring@1',
      '环形',
      '在闭合圆周上均匀排布。',
      radialSchema,
    ),
    schema: radialSchema,
    apply: (nodes, config) => radialApply(nodes, config, true),
  });

  const pathSchema = zod.object({
    points: zod.array(zod.object({
      x: zod.number().finite(),
      y: zod.number().finite(),
    }).strict()).min(2).max(10000),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.path@1',
      '沿路径',
      '按累计弧长在折线路径上等距排布。',
      pathSchema,
      'medium',
      [{ points: [{ x: 0, y: 0 }, { x: 320, y: 180 }] }],
    ),
    schema: pathSchema,
    apply: (nodes, config) => {
      const segments = config.points.slice(1).map((point, index) => {
        const start = config.points[index]!;
        return {
          start,
          end: point,
          length: Math.hypot(point.x - start.x, point.y - start.y),
        };
      });
      const total = segments.reduce((sum, segment) => sum + segment.length, 0);
      const positions = new Map<string, ScenePoint>();
      nodes.forEach((node, index) => {
        let distance = total * (nodes.length <= 1 ? 0 : index / (nodes.length - 1));
        const segment = segments.find((item) => {
          if (distance <= item.length)
            return true;
          distance -= item.length;
          return false;
        }) ?? segments.at(-1)!;
        const ratio = segment.length === 0 ? 0 : distance / segment.length;
        positions.set(node.id, {
          x: segment.start.x + (segment.end.x - segment.start.x) * ratio,
          y: segment.start.y + (segment.end.y - segment.start.y) * ratio,
        });
      });
      return positioned(nodes, positions);
    },
  });

  const partitionSchema = baseBoxSchema.extend({
    weights: zod.record(zod.string(), zod.number().positive()).default({}),
    axis: zod.enum(['x', 'y']).default('x'),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.partition@1',
      '比例分区',
      '按节点权重沿轴分配连续区域。',
      partitionSchema,
    ),
    schema: partitionSchema,
    apply: (nodes, config) => {
      const weights = nodes.map(node => config.weights[node.id] ?? 1);
      const total = weights.reduce((sum, weight) => sum + weight, 0);
      let cursor = config.axis === 'x' ? config.x : config.y;
      const span = config.axis === 'x' ? config.width : config.height;
      const positions = new Map<string, ScenePoint>();
      const spans = new Map<string, number>();
      nodes.forEach((node, index) => {
        const nodeSpan = span * weights[index]! / total;
        positions.set(node.id, {
          x: config.axis === 'x' ? cursor : config.x,
          y: config.axis === 'y' ? cursor : config.y,
        });
        spans.set(node.id, nodeSpan);
        cursor += nodeSpan;
      });
      return positioned(nodes, positions).map((node) => {
        const nodeSpan = spans.get(node.id)!;
        if (node.geometry?.type !== 'rect')
          return node;
        return {
          ...node,
          geometry: {
            ...node.geometry,
            width: config.axis === 'x' ? nodeSpan : node.geometry.width,
            height: config.axis === 'y' ? nodeSpan : node.geometry.height,
          },
          bounds: {
            ...node.bounds,
            width: config.axis === 'x' ? nodeSpan : node.bounds.width,
            height: config.axis === 'y' ? nodeSpan : node.bounds.height,
          },
        };
      });
    },
  });

  const packSchema = baseBoxSchema.extend({
    cellSize: zod.number().positive().max(10000).default(40),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.pack@1',
      '确定性紧凑排列',
      '使用固定单元与稳定顺序进行近似 packing。',
      packSchema,
      'medium',
    ),
    schema: packSchema,
    apply: (nodes, config) => {
      const columns = Math.max(
        1,
        Math.floor((config.width - config.padding * 2) / config.cellSize),
      );
      return positioned(nodes, new Map(nodes.map((node, index) => [
        node.id,
        {
          x: config.x + config.padding + (index % columns) * config.cellSize,
          y: config.y + config.padding
            + Math.floor(index / columns) * config.cellSize,
        },
      ])));
    },
  });

  const treeSchema = baseBoxSchema.extend({
    orientation: zod.enum(['vertical', 'horizontal']).default('vertical'),
    levelGap: zod.number().positive().max(10000).default(100),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.tree@1',
      '层级树',
      '按 parentId 深度和稳定顺序排布树节点。',
      treeSchema,
      'medium',
    ),
    schema: treeSchema,
    apply: (nodes, config) => {
      const ids = new Set(nodes.map(node => node.id));
      const depthOf = (node: NormalizedSceneNode) => {
        let depth = 0;
        let current = node;
        const visited = new Set<string>();
        while (current.parentId && ids.has(current.parentId)) {
          if (visited.has(current.id))
            break;
          visited.add(current.id);
          depth += 1;
          current = nodes.find(item => item.id === current.parentId) ?? current;
        }
        return depth;
      };
      const levels = new Map<number, NormalizedSceneNode[]>();
      for (const node of nodes) {
        const depth = depthOf(node);
        levels.set(depth, [...(levels.get(depth) ?? []), node]);
      }
      const positions = new Map<string, ScenePoint>();
      for (const [depth, levelNodes] of levels) {
        levelNodes.forEach((node, index) => {
          const cross = (index + 1) / (levelNodes.length + 1);
          positions.set(node.id, config.orientation === 'vertical'
            ? {
                x: config.x + config.width * cross,
                y: config.y + config.padding + depth * config.levelGap,
              }
            : {
                x: config.x + config.padding + depth * config.levelGap,
                y: config.y + config.height * cross,
              });
        });
      }
      return positioned(nodes, positions);
    },
  });

  const dagreSchema = baseBoxSchema.extend({
    rankDirection: zod.enum(['TB', 'BT', 'LR', 'RL']).default('LR'),
    rankGap: zod.number().positive().max(10000).default(80),
    nodeGap: zod.number().positive().max(10000).default(40),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.dag@1',
      '有向图',
      '通过受控 Dagre 封装排布有向图。',
      dagreSchema,
      'high',
      [{}],
      [`单次最多处理 ${SCENE_LIMITS.maxDagLayoutNodes} 个节点`],
    ),
    schema: dagreSchema,
    apply: (nodes, config, context) => {
      if (nodes.length > SCENE_LIMITS.maxDagLayoutNodes) {
        throw new Error(
          `有向图布局节点数 ${nodes.length} 超过上限 ${SCENE_LIMITS.maxDagLayoutNodes}`,
        );
      }
      const graph = new dagre.graphlib.Graph();
      graph.setGraph({
        rankdir: config.rankDirection,
        ranksep: config.rankGap,
        nodesep: config.nodeGap,
        marginx: config.padding,
        marginy: config.padding,
      });
      graph.setDefaultEdgeLabel(() => ({}));
      for (const node of nodes) {
        const size = nodeSize(node);
        graph.setNode(node.id, size);
      }
      const ids = new Set(nodes.map(node => node.id));
      for (const relation of context.relations) {
        if (
          relation.type === 'connect'
          && ids.has(relation.sourceId)
          && ids.has(relation.targetId)
        ) {
          graph.setEdge(relation.sourceId, relation.targetId);
        }
      }
      dagre.layout(graph);
      const positions = new Map<string, ScenePoint>();
      for (const node of nodes) {
        const result = graph.node(node.id) as {
          x: number;
          y: number;
          width: number;
          height: number;
        };
        positions.set(node.id, {
          x: config.x + result.x - result.width / 2,
          y: config.y + result.y - result.height / 2,
        });
      }
      return positioned(nodes, positions);
    },
  });

  const forceSchema = baseBoxSchema.extend({
    iterations: zod.number().int().positive().max(SCENE_LIMITS.maxLayoutIterations).default(120),
    linkDistance: zod.number().positive().max(10000).default(100),
    repulsion: zod.number().positive().max(1000000).default(800),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.force@1',
      '确定性力导向',
      '固定初始化、迭代次数和顺序的可复现力导向布局。',
      forceSchema,
      'high',
      [{}],
      [
        `O(n²) 斥力计算单次最多处理 ${SCENE_LIMITS.maxForceLayoutNodes} 个节点`,
        `迭代次数最多 ${SCENE_LIMITS.maxLayoutIterations}`,
      ],
    ),
    schema: forceSchema,
    apply: (nodes, config, context) => {
      if (nodes.length > SCENE_LIMITS.maxForceLayoutNodes) {
        throw new Error(
          `力导向布局节点数 ${nodes.length} 超过上限 ${SCENE_LIMITS.maxForceLayoutNodes}`,
        );
      }
      const positions = new Map<string, ScenePoint>(nodes.map((node, index) => {
        const angle = index * 2.399963229728653;
        const radius = 20 * Math.sqrt(index + 1);
        return [node.id, {
          x: config.x + config.width / 2 + Math.cos(angle) * radius,
          y: config.y + config.height / 2 + Math.sin(angle) * radius,
        }];
      }));
      const ids = new Set(nodes.map(node => node.id));
      const edges = context.relations.filter(relation =>
        relation.type === 'connect'
        && ids.has(relation.sourceId)
        && ids.has(relation.targetId),
      );
      for (let iteration = 0; iteration < config.iterations; iteration += 1) {
        const movement = new Map(nodes.map(node => [
          node.id,
          { x: 0, y: 0 },
        ]));
        for (let leftIndex = 0; leftIndex < nodes.length; leftIndex += 1) {
          for (
            let rightIndex = leftIndex + 1;
            rightIndex < nodes.length;
            rightIndex += 1
          ) {
            const left = positions.get(nodes[leftIndex]!.id)!;
            const right = positions.get(nodes[rightIndex]!.id)!;
            const dx = right.x - left.x || 0.001;
            const dy = right.y - left.y || 0.001;
            const distanceSquared = Math.max(dx * dx + dy * dy, 1);
            const force = config.repulsion / distanceSquared;
            const distance = Math.sqrt(distanceSquared);
            const fx = dx / distance * force;
            const fy = dy / distance * force;
            movement.get(nodes[leftIndex]!.id)!.x -= fx;
            movement.get(nodes[leftIndex]!.id)!.y -= fy;
            movement.get(nodes[rightIndex]!.id)!.x += fx;
            movement.get(nodes[rightIndex]!.id)!.y += fy;
          }
        }
        for (const edge of edges) {
          const source = positions.get(edge.sourceId)!;
          const target = positions.get(edge.targetId)!;
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const distance = Math.max(Math.hypot(dx, dy), 1);
          const force = (distance - config.linkDistance) * 0.02;
          const fx = dx / distance * force;
          const fy = dy / distance * force;
          movement.get(edge.sourceId)!.x += fx;
          movement.get(edge.sourceId)!.y += fy;
          movement.get(edge.targetId)!.x -= fx;
          movement.get(edge.targetId)!.y -= fy;
        }
        for (const node of nodes) {
          const point = positions.get(node.id)!;
          const delta = movement.get(node.id)!;
          point.x = Math.min(
            config.x + config.width,
            Math.max(config.x, point.x + delta.x),
          );
          point.y = Math.min(
            config.y + config.height,
            Math.max(config.y, point.y + delta.y),
          );
        }
      }
      return positioned(nodes, positions);
    },
  });

  const alignSchema = zod.object({
    axis: zod.enum(['x', 'y']),
    value: zod.number().finite(),
    distribution: zod.enum(['none', 'equal']).default('none'),
    gap: zod.number().nonnegative().max(10000).default(16),
  }).strict();
  registry.register({
    descriptor: descriptor(
      'probfun.scene.layout.align@1',
      '锚点与约束对齐',
      '沿指定轴对齐，并可等距分布。',
      alignSchema,
      'low',
      [{ axis: 'x', value: 0 }],
    ),
    schema: alignSchema,
    apply: (nodes, config) => {
      let cursor = 0;
      const positions = new Map<string, ScenePoint>();
      for (const node of nodes) {
        positions.set(node.id, {
          x: config.axis === 'x'
            ? config.value
            : config.distribution === 'equal' ? cursor : node.transform.x,
          y: config.axis === 'y'
            ? config.value
            : config.distribution === 'equal' ? cursor : node.transform.y,
        });
        cursor += (
          config.axis === 'x'
            ? nodeSize(node).height
            : nodeSize(node).width
        ) + config.gap;
      }
      return positioned(nodes, positions);
    },
  });

  return registry;
}
